import React, { useEffect } from "react";
import { useFieldArray, useForm, Controller } from "react-hook-form";
import ReactSelect from "react-select";
import { Plus, Trash2, IndianRupee } from "lucide-react";
import { modalSelectStyles } from "../../static/styles";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { t } from "../../locales";
import { ProcessRateFormValues, ProcessRateRow, ProcessRateOptionProps } from "../../types/processRate";

const ALL_OPTION_VALUE = 0;

type Props = {
  rows: ProcessRateRow[];
  subProcessOptions: ProcessRateOptionProps[];
  itemOptions: ProcessRateOptionProps[];
  onSave: (rows: ProcessRateRow[]) => void;
  saving: boolean;
};

const toFormRows = (rows: ProcessRateRow[]): ProcessRateRow[] =>
  rows.length > 0
    ? rows.map((r) => ({
        sub_process_id: r.sub_process_id ?? ALL_OPTION_VALUE,
        item_id: r.item_id ?? ALL_OPTION_VALUE,
        rate: r.rate,
      }))
    : [{ sub_process_id: ALL_OPTION_VALUE, item_id: ALL_OPTION_VALUE, rate: "" }];

const ProcessRateEditor = ({ rows, subProcessOptions, itemOptions, onSave, saving }: Props) => {
  const { control, register, handleSubmit, reset, formState: { errors } } = useForm<ProcessRateFormValues>({
    defaultValues: { rates: toFormRows(rows) },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "rates" });

  useEffect(() => {
    reset({ rates: toFormRows(rows) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const subProcessSelectOptions = [
    { value: ALL_OPTION_VALUE, label: t.processRates.allSubProcesses },
    ...subProcessOptions,
  ];
  const itemSelectOptions = [
    { value: ALL_OPTION_VALUE, label: t.processRates.allItems },
    ...itemOptions,
  ];

  const submit = handleSubmit((data) => {
    const payload: ProcessRateRow[] = data.rates.map((row) => ({
      sub_process_id: Number(row.sub_process_id) === ALL_OPTION_VALUE ? null : Number(row.sub_process_id),
      item_id: Number(row.item_id) === ALL_OPTION_VALUE ? null : Number(row.item_id),
      rate: Number(row.rate),
    }));
    onSave(payload);
  });

  return (
    <form onSubmit={submit} className="space-y-3">
      {fields.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-4">{t.processRates.noRows}</p>
      )}

      {fields.map((field, index) => (
        <div key={field.id} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_160px_40px] gap-2 items-end border rounded-lg p-3 bg-gray-50/50">
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">{t.processRates.subProcessLabel}</Label>
            <Controller
              control={control}
              name={`rates.${index}.sub_process_id`}
              render={({ field: f }) => (
                <ReactSelect
                  styles={modalSelectStyles as any}
                  options={subProcessSelectOptions}
                  value={subProcessSelectOptions.find((o) => o.value === Number(f.value)) || subProcessSelectOptions[0]}
                  onChange={(opt: any) => f.onChange(opt?.value ?? ALL_OPTION_VALUE)}
                />
              )}
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-gray-500">{t.processRates.itemLabel}</Label>
            <Controller
              control={control}
              name={`rates.${index}.item_id`}
              render={({ field: f }) => (
                <ReactSelect
                  styles={modalSelectStyles as any}
                  options={itemSelectOptions}
                  value={itemSelectOptions.find((o) => o.value === Number(f.value)) || itemSelectOptions[0]}
                  onChange={(opt: any) => f.onChange(opt?.value ?? ALL_OPTION_VALUE)}
                />
              )}
            />
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-gray-500">{t.processRates.rateLabel}</Label>
            <div className="relative">
              <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input
                type="number"
                min={0}
                step="0.01"
                className="h-[44px] pl-7"
                {...register(`rates.${index}.rate`, { required: true, min: 0 })}
              />
            </div>
            {errors.rates?.[index]?.rate && (
              <span className="text-xs text-red-500">{t.processRates.rateRequired}</span>
            )}
          </div>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-[44px] w-[40px] rounded-lg hover:bg-red-50"
            onClick={() => remove(index)}
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      ))}

      <div className="flex items-center justify-between pt-1">
        <Button
          type="button"
          variant="outline"
          className="h-10 rounded-lg border-[#113F67]/30 text-[#113F67]"
          onClick={() => append({ sub_process_id: ALL_OPTION_VALUE, item_id: ALL_OPTION_VALUE, rate: "" })}
        >
          <Plus className="h-4 w-4 mr-1" /> {t.processRates.addRow}
        </Button>

        <Button
          type="submit"
          className="h-10 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f]"
          disabled={saving}
        >
          {saving ? t.processRates.saving : t.processRates.save}
        </Button>
      </div>
    </form>
  );
};

export default ProcessRateEditor;
